/**
 * "Empty rows: Show | Hide" (DOP-609).
 *
 * It replaced a single pressed-or-not button labelled "Hide empty rows". On
 * the Summit call that button's state was unreadable: highlighted meant the
 * rows were HIDDEN, so the label described the opposite of what the grid in
 * front of the analyst was doing half the time. Two named options with the
 * current one selected cannot be read backwards.
 *
 * A radio group, because that is what it is — two mutually exclusive answers
 * to one question — and screen readers announce "Show, 1 of 2, selected".
 */

import React from "react";

export interface KpiEmptyRowsToggleProps {
	hideEmptyRows: boolean;
	onChange: (hide: boolean) => void;
}

const OPTIONS: ReadonlyArray<{ label: string; hide: boolean }> = [
	{ label: "Show", hide: false },
	{ label: "Hide", hide: true },
];

const KpiEmptyRowsToggle: React.FC<KpiEmptyRowsToggleProps> = ({
	hideEmptyRows,
	onChange,
}) => (
	<div className="flex items-center gap-2">
		<span id="kpi-empty-rows-label" className="text-sm font-medium text-[#3f4247]">
			Empty rows
		</span>
		<div
			role="radiogroup"
			aria-labelledby="kpi-empty-rows-label"
			className="inline-flex gap-0.5 rounded-lg bg-[#f2f2f2] p-[3px]"
		>
			{OPTIONS.map((option) => {
				const active = option.hide === hideEmptyRows;
				return (
					<button
						key={option.label}
						type="button"
						role="radio"
						aria-checked={active}
						onClick={() => onChange(option.hide)}
						className={`h-7 whitespace-nowrap rounded-md px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#008782] ${
							active
								? "bg-white font-semibold text-[#111111] shadow-[0_1px_2px_rgba(17,24,39,0.14)]"
								: "text-text-muted hover:text-text-base"
						}`}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	</div>
);

export default React.memo(KpiEmptyRowsToggle);
